import classNames from 'classnames';
import React, { useMemo } from 'react';

import { ErrorCard } from '@eika/card';
import { Icon, IconTypes } from '@eika/icon';

import { InterestedParty } from '../../../interfaces/ConsentAssignmentTypes';
import { useTranslation } from '../../../translations';
import { formatTranslation } from '../../../translations/useTranslation';
import { ConsentAssignmentWrapper } from '../ConsentAssignmentWrapper';

import './ConsentAssignmentTasks.scss';

interface ConsentAssignmentTasksInterface {
    deadlineHasPassed: boolean;
    taskUrl: string;
    interestedParty: string;
    bankName: string;
    namePrincipalBorrower: string;
    taskIsDone: boolean;
}

const ConsentAssignmentTasks = ({
    interestedParty,
    deadlineHasPassed,
    taskUrl,
    bankName,
    namePrincipalBorrower,
    taskIsDone,
}: ConsentAssignmentTasksInterface) => {
    const { task, consentAssignment } = useTranslation();

    const bodyText = useMemo(() => {
        if (taskIsDone) return consentAssignment.bodyText.ACCEPTED;
        switch (interestedParty) {
            case InterestedParty.KAUSJONIST:
                return formatTranslation(consentAssignment.bodyText.KAUSJONIST, namePrincipalBorrower, bankName);
            case InterestedParty.HOVEDLAANTAKER:
            case InterestedParty.INNEHAVER:
                return formatTranslation(
                    consentAssignment.bodyText.HOVEDLAANTAKER,
                    bankName,
                    consentAssignment.interestedParty[interestedParty],
                );
            default:
                return formatTranslation(
                    consentAssignment.bodyText.DEFAULT,
                    bankName,
                    consentAssignment.interestedParty[
                        interestedParty === InterestedParty.MEDLAANTAKER ? InterestedParty.MEDLAANTAKER : 'DEFAULT'
                    ],
                    namePrincipalBorrower,
                );
        }
    }, [
        bankName,
        consentAssignment.bodyText.ACCEPTED,
        consentAssignment.bodyText.DEFAULT,
        consentAssignment.bodyText.HOVEDLAANTAKER,
        consentAssignment.bodyText.KAUSJONIST,
        consentAssignment.interestedParty,
        interestedParty,
        namePrincipalBorrower,
        taskIsDone,
    ]);

    if (deadlineHasPassed)
        return (
            <ErrorCard
                className="deadline-passed-card"
                title={task.deadlinePassedTitle}
                description={task.deadlinePassedMessage}
            />
        );
    return (
        <div className="consent-assignment-tasks">
            <span className="consent-assignment-tasks__title typography-subtitle">{task.tasksTitle}</span>
            <ConsentAssignmentWrapper taskUrl={taskUrl} taskIsDone={taskIsDone}>
                <Icon
                    wrapperClassName={classNames('consent-assignment-link__checkmark-icon', {
                        'consent-assignment-link__checkmark-icon--completed': taskIsDone,
                    })}
                    type={IconTypes.CHECKMARK_TIL_SIRKEL_24}
                />
                <div className="consent-assignment-link__text-wrapper">
                    <span className="typography-subtitle">
                        {taskIsDone ? consentAssignment.completedTitle : task.documentsForSigning}
                    </span>
                    <span className="typography-small">{bodyText}</span>
                </div>
                {!taskIsDone && <Icon className="task-link-arrow-icon icon--24" type={IconTypes.PIL_HOEYRE} />}
            </ConsentAssignmentWrapper>
        </div>
    );
};

export default ConsentAssignmentTasks;
